import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Lock, Eye, AlertTriangle, Check, RotateCcw } from 'lucide-react';
import { auth } from '../../lib/firebase';
import { authenticatedFetch, ApiError } from './authenticatedApiHelper';
import { classifyError, ClassifiedError } from './documentReviewErrors';

interface DocumentRecord {
  id: string;
  document_type: string;
  original_filename: string;
  mime_type: string;
  byte_size: number;
  review_status: string;
  uploaded_at_utc: string;
  review_notes?: string;
  merchant_tenant_id?: string;
  share_token?: string;
}

interface EvidenceUploadProps {
  activeRole: string;
  tenantId: string;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const EvidenceUpload: React.FC<EvidenceUploadProps> = ({ activeRole, tenantId, onAddLog }) => {
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [documentType, setDocumentType] = useState<string>('Insurance');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [redactionConfirmed, setRedactionConfirmed] = useState<boolean>(false);
  
  // Loading & Error States
  const [loading, setLoading] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [loadingDocId, setLoadingDocId] = useState<string | null>(null);
  const [detailedError, setDetailedError] = useState<ClassifiedError | null>(null);
  const [uploadError, setUploadError] = useState<ClassifiedError | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Administrative Review Input Box Reasons
  const [rejReasons, setRejReasons] = useState<Record<string, string>>({});

  // Verified claims from active Firebase Auth token
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [verifiedClaims, setVerifiedClaims] = useState<{ role?: string; tenantId?: string }>({});

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult();
          setVerifiedClaims({
            role: (tokenResult.claims.role as string) || undefined,
            tenantId: (tokenResult.claims.tenantId as string) || (tokenResult.claims.tenant_id as string) || undefined,
          });
        } catch (e) {
          console.error('[NEXORAX AUTH] Error listing user claims result:', e);
          setVerifiedClaims({});
        }
      } else {
        setVerifiedClaims({});
      }
    });
    return () => unsubscribe();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    setDetailedError(null);
    try {
      const payload = await authenticatedFetch('/api/direct-delivery/documents');
      setDocuments(payload.data || []);
    } catch (e: any) {
      const classified = classifyError(e);
      setDetailedError(classified);
      // Keep existing document list during sync failure (Do not clear documents list!)
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [currentUser, verifiedClaims]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDetailedError(null);
    setUploadError(null);
    setSuccessMsg(null);
    const files = e.target.files;
    if (!files || files.length === 0) {
      setSelectedFile(null);
      return;
    }

    const file = files[0];
    
    if (file.size > 5 * 1024 * 1024) {
      setUploadError({
        title: 'Document rejected',
        message: 'File exceeds 5 MB',
        isRetryable: false
      });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const validMimes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    if (!validMimes.includes(file.type)) {
      setUploadError({
        title: 'Document rejected',
        message: 'Unsupported file type',
        isRetryable: false
      });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (uploading) return;
    setDetailedError(null);
    setUploadError(null);
    setSuccessMsg(null);
    
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    if (file.size > 5 * 1024 * 1024) {
      setUploadError({
        title: 'Document rejected',
        message: 'File exceeds 5 MB',
        isRetryable: false
      });
      return;
    }

    const validMimes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    if (!validMimes.includes(file.type)) {
      setUploadError({
        title: 'Document rejected',
        message: 'Unsupported file type',
        isRetryable: false
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (uploading) return;
    if (!selectedFile) {
      setUploadError({
        title: 'Submission Error',
        message: 'Please select a document file to upload.',
        isRetryable: false
      });
      return;
    }
    if (!redactionConfirmed) {
      setUploadError({
        title: 'Submission Error',
        message: 'Couriers must confirm that uploaded statements have been redacted.',
        isRetryable: false
      });
      return;
    }

    setUploading(true);
    setDetailedError(null);
    setUploadError(null);
    setSuccessMsg(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', documentType);
      formData.append('redaction_confirmed', 'true');

      await authenticatedFetch('/api/direct-delivery/documents', {
        method: 'POST',
        body: formData,
      });

      setSuccessMsg(`Document uploaded successfully and queued for verification.`);
      onAddLog(
        'Evidence Vault',
        `Uploaded structural credentials: ${selectedFile.name} (${documentType})`,
        'SUCCESS',
        'Private hash validation completed successfully.'
      );
      
      // Successfully uploaded! Clear file selection and files
      setSelectedFile(null);
      setRedactionConfirmed(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadDocuments();
    } catch (err: any) {
      const classified = classifyError(err);
      setUploadError(classified);
      onAddLog('Evidence Vault', 'Failed document upload task.', 'DENIED', classified.message);
      // Keep selectedFile model intact on error so user can correct or retry easily
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId: string) => {
    if (loadingDocId) return;
    if (!window.confirm("Are you sure you want to permanently delete this credential document? This is irreversible.")) {
      return;
    }
    setDetailedError(null);
    setUploadError(null);
    setSuccessMsg(null);
    setLoadingDocId(docId);

    try {
      await authenticatedFetch(`/api/direct-delivery/documents/${docId}`, {
        method: 'DELETE'
      });
      setSuccessMsg("Document deleted successfully from private vault.");
      onAddLog('Evidence Vault', `Credential deleted: ID ${docId}`, 'WARNING', 'Physical file record deleted.');
      await loadDocuments();
    } catch (e: any) {
      setDetailedError(classifyError(e));
    } finally {
      setLoadingDocId(null);
    }
  };

  const handleToggleShare = async (doc: DocumentRecord) => {
    if (loadingDocId) return;
    setDetailedError(null);
    setUploadError(null);
    setSuccessMsg(null);
    setLoadingDocId(doc.id);

    const isShared = !!doc.share_token;
    const url = isShared 
      ? `/api/direct-delivery/documents/${doc.id}/revoke-share`
      : `/api/direct-delivery/documents/${doc.id}/share`;

    try {
      await authenticatedFetch(url, {
        method: 'POST',
        body: {
          merchant_tenant_id: 'ALL'
        }
      });
      setSuccessMsg(isShared ? "Removed merchant access hash token." : "Generated active merchant secure share token.");
      await loadDocuments();
    } catch (e: any) {
      setDetailedError(classifyError(e));
    } finally {
      setLoadingDocId(null);
    }
  };

  // Admin and Super Admin Review submission
  const handleReviewSubmit = async (docId: string, status: 'APPROVED' | 'REJECTED') => {
    if (loadingDocId) return;

    const reason = rejReasons[docId] || '';
    if (status === 'REJECTED' && !reason.trim()) {
      setDetailedError({
        title: 'Document rejected',
        message: 'Missing rejection reason',
        isRetryable: false
      });
      return;
    }

    setDetailedError(null);
    setUploadError(null);
    setSuccessMsg(null);
    setLoadingDocId(docId);

    try {
      await authenticatedFetch(`/api/admin/direct-delivery/documents/${docId}/review`, {
        method: 'POST',
        body: { status, reason }
      });
      setSuccessMsg(`Document has been marked as ${status} successfully.`);
      onAddLog('Evidence Vault', `Review outcome determined: ID ${docId}`, 'SUCCESS', `Set status to ${status}.`);
      await loadDocuments();
    } catch (e: any) {
      setDetailedError(classifyError(e));
    } finally {
      setLoadingDocId(null);
    }
  };

  const formatByteSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Determine if the current role in the UI is authenticated and admin
  const isCertifiedAdmin = verifiedClaims.role === 'admin' || verifiedClaims.role === 'super_admin';

  return (
    <div id="evidence-upload-vault-root" className="bg-zinc-950/85 p-5 rounded-3xl border border-[#00ffff]/20 space-y-4 text-left">
      <div className="flex justify-between items-center border-b border-white/5 pb-2">
        <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-1.5">
          <Lock className="w-4 h-4 text-[#00ffff]" />
          <span>NEXORAX Real Private Evidence Vault</span>
        </h3>
        <span className="text-[8.5px] font-mono text-zinc-500 uppercase tracking-widest bg-zinc-900 border border-white/5 px-2 py-0.5 rounded">
          {currentUser ? "VERIFIED FIREBASE SECURE" : "DEV TRIAL SANDBOX MOCK"}
        </span>
      </div>

      {/* ERROR / SUCCESS ALERTS WITH accessible role="alert" */}
      {detailedError && (
        <div role="alert" className="p-3 bg-rose-500/10 border border-rose-500/20 text-rose-400 text-[10px] font-mono rounded-lg flex flex-col gap-2 text-left">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <strong className="block text-[10px] uppercase font-black tracking-wider">{detailedError.title}</strong>
              <p className="mt-0.5">{detailedError.message}</p>
            </div>
          </div>
          {detailedError.isRetryable && (
            <button 
              type="button"
              onClick={loadDocuments}
              className="px-2.5 py-1 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 rounded font-bold text-[9px] uppercase tracking-wider transition-colors cursor-pointer self-start"
            >
              Retry Sync
            </button>
          )}
        </div>
      )}

      {uploadError && (
        <div role="alert" className="p-3 bg-rose-500/10 border border-rose-500/20 text-rose-400 text-[10px] font-mono rounded-lg flex items-start gap-2 text-left">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong className="block text-[10px] uppercase font-black tracking-wider">{uploadError.title}</strong>
            <p className="mt-0.5">{uploadError.message}</p>
          </div>
        </div>
      )}

      {successMsg && (
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-mono rounded-lg flex items-start gap-2 text-left">
          <Check className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* DRAG AND DROP FILE UPLOAD AREA */}
      <form onSubmit={handleUploadSubmit} className="space-y-4">
        <div 
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`p-4 bg-black/60 border border-dashed hover:border-[#00ffff]/40 transition-colors rounded-xl text-center space-y-2.5 ${
            uploading ? 'opacity-50 cursor-not-allowed border-zinc-700' : 'cursor-pointer border-[#00ffff]/20'
          }`}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input 
            type="file"
            ref={fileInputRef}
            disabled={uploading}
            onChange={handleFileChange}
            accept="image/png, image/jpeg, application/pdf"
            className="hidden"
          />
          <div className="flex justify-center">
            <Upload className="w-7 h-7 text-[#00ffff] opacity-70 animate-pulse" />
          </div>
          <div className="space-y-1">
            <p className="text-[10px] text-zinc-200 font-bold uppercase font-mono">
              {selectedFile ? `📎 Staged: ${selectedFile.name}` : "Drag & Drop compliance files or click to choose"}
            </p>
            <p className="text-[8px] text-zinc-500 uppercase font-mono">
              Accepted: PDF, PNG, JPEG • Max Size Limit: 5 MB
            </p>
          </div>
        </div>

        {selectedFile && (
          <div className="p-3.5 bg-black/40 border border-white/5 rounded-xl space-y-3.5 text-left">
            <div className="grid grid-cols-2 gap-3 text-[10px] font-mono">
              <div>
                <label className="text-zinc-500 text-[8px] block uppercase tracking-wider mb-1">Document Category</label>
                <select
                  value={documentType}
                  disabled={uploading}
                  onChange={(e) => setDocumentType(e.target.value)}
                  className="w-full bg-zinc-900 rounded border border-white/10 p-2 text-white text-[10px] outline-none focus:border-[#00ffff] disabled:opacity-50"
                >
                  <option value="Insurance">Hire & Reward Cover (Insurance)</option>
                  <option value="Earnings">Earnings Statement (Platform History)</option>
                  <option value="Ratings">Aggregator Feedback Scorecard</option>
                  <option value="RightToWork">Identity / Visa Rights</option>
                  <option value="License">Driving Licence Front/Back</option>
                </select>
              </div>
              <div className="flex flex-col justify-end">
                <span className="text-[8px] text-zinc-500 block mb-1">STAGED SIZE:</span>
                <span className="text-white text-[10px] font-bold block">{formatByteSize(selectedFile.size)}</span>
              </div>
            </div>

            <div className="space-y-2.5">
              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input 
                  type="checkbox"
                  disabled={uploading}
                  checked={redactionConfirmed}
                  onChange={(e) => setRedactionConfirmed(e.target.checked)}
                  className="mt-0.5 accent-[#00ffff] disabled:opacity-50"
                />
                <span className="text-[8.5px] text-amber-400 font-mono uppercase leading-snug">
                  🛡️ I confirm that I have redacted sensitive personal addresses and banking values from this snapshot.
                </span>
              </label>

              <button
                type="submit"
                disabled={uploading}
                className="w-full py-2.5 bg-[#00ffff] hover:bg-[#00ffff]/80 text-black font-black uppercase text-center rounded-lg cursor-pointer transition-all text-xs tracking-wider disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed"
              >
                {uploading ? "TRANSMITTING FILE WITH COMPLIANCE HASH..." : "COMMIT FILE TO SECURE STORAGE NODE"}
              </button>
            </div>
          </div>
        )}
      </form>

      {/* DOCUMENT LISTING VAULT */}
      <div className="pt-2 text-left space-y-2.5">
        <span className="text-[9px] font-mono font-black text-zinc-400 uppercase tracking-widest block">
          Current Secure Payload Vault ({documents.length})
        </span>

        {loading ? (
          <div className="p-4 bg-black/40 text-center text-zinc-500 text-[10px] font-mono uppercase">
            Loading private telemetry documents...
          </div>
        ) : documents.length === 0 ? (
          <div className="p-4 rounded-xl bg-black/20 border border-white/5 text-center text-zinc-500 text-[9.5px] font-mono uppercase">
            Vault is currently empty. Upload your vetted proof records above.
          </div>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
            {documents.map((d) => (
              <div key={d.id} className="p-3 bg-[#030612] border border-white/5 rounded-2xl flex flex-col gap-2.5">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <span className="text-white text-[10.5px] font-bold block truncate font-mono">{d.original_filename}</span>
                    <p className="text-[8px] text-zinc-500 uppercase tracking-wide font-mono mt-0.5">
                      Type: <strong className="text-zinc-300">{d.document_type}</strong> ({formatByteSize(d.byte_size)}) • Added {new Date(d.uploaded_at_utc).toLocaleString()}
                    </p>
                  </div>
                  <span className={`text-[7.5px] font-mono uppercase font-bold px-1.5 py-0.5 rounded shrink-0 ${
                    d.review_status === 'APPROVED' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                    d.review_status === 'REJECTED' ? 'bg-rose-500/10 text-rose-500 border border-rose-500/20' :
                    'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                  }`}>
                    {d.review_status}
                  </span>
                </div>

                <div className="flex justify-between items-center text-[8.5px] font-mono border-t border-white/5 pt-2">
                  <span className="text-[7.5px] text-purple-300 uppercase block">
                    {d.share_token ? "🔑 SHARED VIA MERCHANT TOKEN" : "🔒 Private access controlled"}
                  </span>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleToggleShare(docIdToMatch(d))}
                      disabled={!!loadingDocId}
                      className={`px-2 py-0.5 rounded font-black border text-[8px] cursor-pointer transition-colors disabled:opacity-50 ${
                        d.share_token 
                          ? 'bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-600/10' 
                          : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-600/10'
                      }`}
                    >
                      {d.share_token ? 'REVOKE SHARE' : 'ALLOW SHARE'}
                    </button>

                    <button
                      onClick={() => handleDelete(d.id)}
                      disabled={!!loadingDocId}
                      className="p-1 text-rose-400 hover:bg-rose-500/10 border border-rose-500/10 rounded cursor-pointer transition-colors disabled:opacity-50"
                      title="Delete from safe"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {d.review_notes && (
                  <p className="text-[8px] text-rose-400 mt-1 font-mono uppercase leading-normal bg-rose-500/5 p-1.5 rounded border border-rose-500/10">
                    Scribe Inspector Rejection Feedback: {d.review_notes}
                  </p>
                )}

                {/* Secure administrative controls visible ONLY to certified verification roles */}
                {isCertifiedAdmin && (
                  <div className="mt-2.5 p-2 bg-zinc-950/60 rounded-xl border border-white/5 space-y-2">
                    <span className="text-[7.5px] font-mono font-black text-zinc-400 block uppercase tracking-wider">Admin Verification Interface</span>
                    <div className="grid grid-cols-1 gap-1.5">
                      <input 
                        type="text"
                        placeholder="Rejection reason (Required for Reject)"
                        value={rejReasons[d.id] || ''}
                        onChange={(e) => setRejReasons(prev => ({ ...prev, [d.id]: e.target.value }))}
                        disabled={loadingDocId === d.id}
                        className="w-full bg-zinc-900 rounded border border-white/10 p-1.5 text-white text-[9px] outline-none font-mono placeholder:text-zinc-600"
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleReviewSubmit(d.id, 'APPROVED')}
                          disabled={loadingDocId === d.id}
                          className="px-2.5 py-1 bg-emerald-500 text-black font-extrabold text-[8px] uppercase tracking-wider rounded hover:bg-emerald-400 transition-colors cursor-pointer disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleReviewSubmit(d.id, 'REJECTED')}
                          disabled={loadingDocId === d.id || !(rejReasons[d.id] || '').trim()}
                          className="px-2.5 py-1 bg-rose-500 text-white font-extrabold text-[8px] uppercase tracking-wider rounded hover:bg-rose-400 transition-colors cursor-pointer disabled:opacity-50 disabled:bg-zinc-800 disabled:text-zinc-500"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  function docIdToMatch(doc: DocumentRecord) {
    return doc;
  }
};
